import type { CommsApi } from '../source';

/**
 * The verbs the mail-client layout's toolbars call, in one place.
 *
 * They are named for the BUTTON a mail client draws, not for what this product
 * calls the thing underneath — `archive` is our Resolve, `remove` is the
 * internal soft delete — because the whole point of this layout is that
 * somebody who has used Gmail knows what the icons do before reading anything.
 * The mapping is written down once here rather than at each of the nine call
 * sites that would otherwise have to know it.
 *
 * Every one of them is fire-and-forget over an optimistic list: the row is
 * repainted by the `conversation.updated` broadcast that follows, which is the
 * same path the three-pane layout already trusts.
 */
export interface MailActions {
  /** Gmail's Archive. Ours resolves — out of the open views, back on a reply. */
  archive: (displayId: number) => void;
  /** Gmail's bin. Internal and soft; nothing is recalled from the customer. */
  remove: (displayId: number) => void;
  markRead: (displayId: number) => void;
  markUnread: (displayId: number) => void;
  snooze: (displayId: number, hours: number) => void;
  toggleStar: (displayId: number, starred: boolean) => void;
  reload: () => void;
}

export function makeMailActions(
  api: CommsApi,
  { onChanged, onError }: { onChanged: () => void; onError: (message: string) => void },
): MailActions {
  /**
   * A refusal here is silent everywhere else in this layout — the row simply
   * does not move — so it has to be said out loud. `onChanged` runs either way:
   * a failed action still needs the list re-read, or an optimistic repaint
   * stands in for a change the server declined.
   */
  const run = (what: string, call: Promise<unknown>) => {
    void call.then(onChanged, (e: unknown) => {
      onError(e instanceof Error && e.message ? e.message : `Could not ${what}.`);
      onChanged();
    });
  };

  return {
    archive: (id) => run('archive that', api.toggleStatus(id, 'resolved')),
    remove: (id) => run('delete that', api.deleteConversation(id)),
    markRead: (id) => run('mark that read', api.markConversationSeen(id)),
    markUnread: (id) => run('mark that unread', api.markConversationUnread(id)),
    snooze: (id, hours) =>
      run(
        'snooze that',
        api.toggleStatus(id, 'snoozed', new Date(Date.now() + hours * 3600_000).toISOString()),
      ),
    toggleStar: (id, starred) => run('star that', api.toggleStar(id, starred)),
    reload: onChanged,
  };
}
